import { useEffect, useRef } from 'react';

interface SwotData {
  strengths: Array<{ content: string; impact: number }>;
  weaknesses: Array<{ content: string; impact: number }>;
  opportunities: Array<{ content: string; impact: number }>;
  threats: Array<{ content: string; impact: number }>;
}

interface SwotRadarChartProps {
  data: SwotData;
  className?: string;
}

export default function SwotRadarChart({ data, className = '' }: SwotRadarChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set up canvas dimensions - larger for better label readability
    const size = 500;
    canvas.width = size;
    canvas.height = size;

    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size / 2 - 120; // Much more padding for labels

    // Clear canvas
    ctx.clearRect(0, 0, size, size);

    // Calculate total impact scores (count × impact level)
    const calculateTotalScore = (items: Array<{ content: string; impact: number }>) => {
      return items.reduce((total, item) => total + item.impact, 0);
    };

    const swotScores = {
      strengths: calculateTotalScore(data.strengths),
      opportunities: calculateTotalScore(data.opportunities),
      weaknesses: calculateTotalScore(data.weaknesses),
      threats: calculateTotalScore(data.threats),
    };

    // Find the maximum score to scale the chart properly
    const maxScore = Math.max(...Object.values(swotScores), 1);
    const maxRadius = Math.max(maxScore, 10); // Minimum scale of 10

    // SWOT categories with their angles (starting from top, clockwise)
    const categories = [
      { name: 'Strengths', value: swotScores.strengths, angle: -Math.PI / 2, color: '#22c55e' },
      { name: 'Opportunities', value: swotScores.opportunities, angle: 0, color: '#3b82f6' },
      { name: 'Threats', value: swotScores.threats, angle: Math.PI / 2, color: '#ef4444' },
      { name: 'Weaknesses', value: swotScores.weaknesses, angle: Math.PI, color: '#f59e0b' },
    ];

    // Draw grid circles with dynamic scaling
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    const gridLevels = 5;
    for (let i = 1; i <= gridLevels; i++) {
      ctx.beginPath();
      ctx.arc(centerX, centerY, (radius * i) / gridLevels, 0, 2 * Math.PI);
      ctx.stroke();
    }

    // Draw grid lines from center to each category
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    categories.forEach((category) => {
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX + radius * Math.cos(category.angle),
        centerY + radius * Math.sin(category.angle)
      );
      ctx.stroke();
    });

    // Draw category labels with better positioning and readability
    ctx.font = 'bold 14px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    categories.forEach((category) => {
      const labelDistance = radius + 60;
      const x = centerX + labelDistance * Math.cos(category.angle);
      const y = centerY + labelDistance * Math.sin(category.angle);

      // Draw background for better readability
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      const textWidth = ctx.measureText(category.name).width;
      ctx.fillRect(x - textWidth / 2 - 8, y - 10, textWidth + 16, 20);

      // Draw border around text background
      ctx.strokeStyle = category.color;
      ctx.lineWidth = 2;
      ctx.strokeRect(x - textWidth / 2 - 8, y - 10, textWidth + 16, 20);

      // Draw the text
      ctx.fillStyle = category.color;
      ctx.fillText(category.name, x, y);

      // Draw the score below the label
      ctx.font = 'bold 12px sans-serif';
      ctx.fillStyle = '#374151';
      ctx.fillText(`Score: ${category.value}`, x, y + 20);
      ctx.font = 'bold 14px sans-serif'; // Reset font
    });

    // Draw scale indicators at different positions to avoid overlap
    ctx.fillStyle = '#6b7280';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'left';
    for (let i = 1; i <= gridLevels; i++) {
      const labelRadius = (radius * i) / gridLevels;
      const scaleValue = Math.round((maxRadius * i) / gridLevels);
      // Position scale labels at 45-degree angle to avoid overlap with category labels
      const scaleAngle = Math.PI / 4;
      const x = centerX + labelRadius * Math.cos(scaleAngle) + 5;
      const y = centerY + labelRadius * Math.sin(scaleAngle);

      // Background for scale labels
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      const scaleText = scaleValue.toString();
      const scaleWidth = ctx.measureText(scaleText).width;
      ctx.fillRect(x - 2, y - 8, scaleWidth + 4, 16);

      ctx.fillStyle = '#6b7280';
      ctx.fillText(scaleText, x, y);
    }

    // Draw SWOT data polygon
    if (categories.some((cat) => cat.value > 0)) {
      ctx.beginPath();
      categories.forEach((category, index) => {
        const distance = (category.value / maxRadius) * radius;
        const x = centerX + distance * Math.cos(category.angle);
        const y = centerY + distance * Math.sin(category.angle);

        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.closePath();

      // Fill polygon with gradient
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
      gradient.addColorStop(0, 'rgba(59, 130, 246, 0.3)');
      gradient.addColorStop(1, 'rgba(59, 130, 246, 0.1)');
      ctx.fillStyle = gradient;
      ctx.fill();

      // Stroke polygon
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Draw data points with labels
      categories.forEach((category) => {
        const distance = (category.value / maxRadius) * radius;
        const x = centerX + distance * Math.cos(category.angle);
        const y = centerY + distance * Math.sin(category.angle);

        // Draw point
        ctx.beginPath();
        ctx.arc(x, y, 6, 0, 2 * Math.PI);
        ctx.fillStyle = category.color;
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 3;
        ctx.stroke();

        // Draw value label near the point
        if (category.value > 0) {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          ctx.font = 'bold 12px sans-serif';
          ctx.textAlign = 'center';
          const valueText = category.value.toString();
          const valueWidth = ctx.measureText(valueText).width;

          // Position value label slightly inward from the point
          const labelOffset = 15;
          const labelX = x - labelOffset * Math.cos(category.angle);
          const labelY = y - labelOffset * Math.sin(category.angle);

          ctx.fillRect(labelX - valueWidth / 2 - 4, labelY - 8, valueWidth + 8, 16);
          ctx.fillStyle = category.color;
          ctx.fillText(valueText, labelX, labelY);
        }
      });
    }
  }, [data]);

  const hasData = Object.values(data).some((category) => category.length > 0);

  if (!hasData) {
    return (
      <div className={`bg-gray-50 rounded-lg p-6 text-center ${className}`}>
        <p className="text-gray-500">No SWOT data available for radar chart</p>
      </div>
    );
  }

  // Calculate totals for display
  const totals = {
    strengths: data.strengths.reduce((total, item) => total + item.impact, 0),
    weaknesses: data.weaknesses.reduce((total, item) => total + item.impact, 0),
    opportunities: data.opportunities.reduce((total, item) => total + item.impact, 0),
    threats: data.threats.reduce((total, item) => total + item.impact, 0),
  };

  return (
    <div className={`bg-white rounded-lg p-4 ${className}`} data-chart-type="swot">
      <h4 className="text-lg font-semibold text-gray-900 mb-2 text-center">SWOT Impact Analysis</h4>
      <div className="flex justify-center mb-4">
        <canvas ref={canvasRef} className="max-w-full" />
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              Strengths
            </span>
            <span className="font-semibold text-green-700">{totals.strengths}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              Opportunities
            </span>
            <span className="font-semibold text-blue-700">{totals.opportunities}</span>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
              Threats
            </span>
            <span className="font-semibold text-red-700">{totals.threats}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center">
              <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
              Weaknesses
            </span>
            <span className="font-semibold text-yellow-700">{totals.weaknesses}</span>
          </div>
        </div>
      </div>

      <div className="mt-4 text-xs text-center text-gray-500">
        <p>Total Impact Score = Count of items × Impact Level (Low=1, Medium=2, High=3)</p>
      </div>
    </div>
  );
}
